import { CommonInput } from 'components';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FaCheck, FaCrown } from 'react-icons/fa6';
import { RiInformationLine } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';
import { load } from '@cashfreepayments/cashfree-js';
import toast from 'react-hot-toast';

import coupon from '../../assets/img/coupon.svg';
import shield from '../../assets/img/shield.svg';
import logo from "../../assets/img/transport360Logo.svg";
import loadingGif from "../../assets/img/blueloading.gif";
import successGif from "../../assets/img/paymentsucess.gif";
// import pendingGif from "../../assets/img/paymentpending.gif"; 
// import failureGif from "../../assets/img/paymentfailed.gif"; 
import { PaymentBillingModal } from 'modals';
import { UserContext } from 'context';
import { StorageServices } from 'utils';
import { createOrderPayment, paymentVerifyApi } from '_services';


const CASHFREE_MODE = process.env.REACT_APP_CASHFREE_MODE || "sandbox";
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";
const STAGING_API_URL = process.env.REACT_APP_STAGING_API_URL || "https://transportapi.urja360.com";
const GST_RATE_DIVISOR = 100;

const initialFormData = {
  company_id: StorageServices.getCompanyId(),
  order_id: '',
  total_years: 1,
  total_amount: 0,
  plan_price: 0,
  gst_price: 0,
  coupon: '',
  selected_subscription_id: '',
  customised_plan: {
    branches: 0,
    users: 0,
  },
  gst_details: {
    gst_number: '',
    company_name: '',
    email_address: '',
  },
  billing_address: {
    address: '',
    city: '',
    state: '',
    pin_code: '',
  },
};

const SubscriptionPlan = () => {
  const navigate = useNavigate();
  const { subscriptionDetails, setSubscriptionDetails } = useContext(UserContext);

  const [formData, setFormData] = useState(initialFormData);
  const [branches, setBranches] = useState(0);
  const [users, setUsers] = useState(0);
  const [hasGST, setHasGST] = useState(true);
  const [isOpenBillingModal, setIsOpenBillingModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showPending, setShowPending] = useState(false);
  const [showFailure, setShowFailure] = useState(false);
  const isUserLimitReached = useMemo(() =>
    subscriptionDetails?.max_no_of_user === users,
    [subscriptionDetails?.max_no_of_user, users]
  );

  const generateOrderId = useCallback(() => {
    const now = new Date();
    const dateStr = now.getFullYear() +
      String(now.getMonth() + 1).padStart(2, '0') +
      String(now.getDate()).padStart(2, '0');
    const timeStr = String(now.getHours()).padStart(2, '0') +
      String(now.getMinutes()).padStart(2, '0') +
      String(now.getSeconds()).padStart(2, '0');
    return `order_${dateStr}_${timeStr}`;
  }, []);

  const calculateTotal = useCallback((subscriptionDetails, branches, users, gstRate) => {
    if (!subscriptionDetails) {
      return { plan_price: 0, gst_price: 0, total_amount: 0 };
    }

    const { yearly_rate, branch_rate, user_rate } = subscriptionDetails;
    const planPrice = yearly_rate + (branch_rate * branches) + (user_rate * users);
    const gstPrice = planPrice * gstRate;
    const totalAmount = planPrice + gstPrice;

    return {
      plan_price: Number(planPrice.toFixed(2)),
      gst_price: Number(gstPrice.toFixed(2)),
      total_amount: Number(totalAmount.toFixed(2)),
    };
  }, []);

  const formatIndianNumber = useCallback((num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  }, []);

  const handleChange = useCallback((section, field, value) => {
    const parsedValue = (typeof value === 'string' && !isNaN(value) && value !== '')
      ? (parseFloat(value) || parseInt(value, 10))
      : value;

    setFormData((prev) => ({
      ...prev,
      ...(section === null
        ? { [field]: parsedValue }
        : { [section]: { ...prev[section], [field]: parsedValue } }
      ),
    }));
  }, []);

  const clearBillingAddress = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      billing_address: {
        address: '',
        city: '',
        state: '',
        pin_code: '',
      },
    }));
  }, []);

  const handleBranchChange = useCallback((increment) => {
    const newBranches = Math.max(0, branches + increment);
    setBranches(newBranches);
    handleChange('customised_plan', 'branches', newBranches);
  }, [branches, handleChange]);

  const handleUserChange = useCallback((increment) => {
    if (increment > 0 && isUserLimitReached) {
      toast.error(`You can add maximum ${subscriptionDetails?.max_no_of_user} users in this plan`);
      return;
    }
    const newUsers = Math.max(0, users + increment);
    setUsers(newUsers);
    handleChange('customised_plan', 'users', newUsers);
  }, [users, isUserLimitReached, subscriptionDetails?.max_no_of_user, handleChange]);

  const paymentVerifyHandle = useCallback(async (orderId, isForceTest = false, testStatus = null) => {
    const payload = {
      order_id: orderId,
      company_id: StorageServices.getCompanyId(),
      
    };

    try {
      setVerifyLoading(true);

      let res;

      // For testing purposes - you can force specific responses
      if (isForceTest && testStatus !== null) {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Create mock response based on testStatus
        res = {
          responseCode: 200,
          data: { status: testStatus }
        };
      } else {
        // Normal API call
        res = await paymentVerifyApi(payload);
      }

      if (res?.responseCode === 200) {
        const { status } = res.data;

        if (status === 1) {
          // Payment successful
          setShowSuccess(true);
          toast.success("Payment completed successfully!");
          setTimeout(() => {
            setShowSuccess(false);
            // Navigate to dashboard or success page
            // navigate('/dashboard');
          }, 3000);

        } else if (status === 0) {
          // Payment pending
          setShowPending(true);
          toast.error("Payment is still pending. Please wait or contact support.");
          setTimeout(() => {
            setShowPending(false);

            // Optional: Auto-recheck after some time (only for real payments)
            if (!isForceTest) {
              setTimeout(() => {
                paymentVerifyHandle(orderId);
              }, 10000); // Recheck after 10 seconds
            }
          }, 3000);

        } else {
          // Payment failed
          setShowFailure(true);
          toast.error("Payment failed. Please try again or contact support.");
          setTimeout(() => {
            setShowFailure(false);
          }, 3000);
        }
      } else {
        // API call failed
        setShowFailure(true);
        toast.error("Unable to verify payment. Please contact support.");
        setTimeout(() => {
          setShowFailure(false);
        }, 3000);
      }
    } catch (error) {
      console.error("Payment verification error:", error);

      // For network errors, treat as pending in case payment actually went through
      setShowPending(true);
      toast.error("Unable to verify payment status. Please contact support if amount was debited.");
      setTimeout(() => {
        setShowPending(false);
      }, 3000);
    } finally {
      setVerifyLoading(false);
    }
  }, []);

  const initializeCashfreePayment = useCallback(async (paymentSessionId, orderId) => {
    let timeoutId;
    let isPaymentCompleted = false;

    try {
      const cashfree = await load({ mode: CASHFREE_MODE });

      const checkoutOptions = {
        paymentSessionId,
        redirectTarget: "_modal",

        // Appearance customization
        appearance: {
          width: "500px",
          height: "650px",
          primaryColor: "#3B82F6",
          backgroundColor: "#FFFFFF",

          theme: {
            primaryColor: "#3B82F6",
            secondaryColor: "#F3F4F6",
            backgroundColor: "#FFFFFF",
            textColor: "#1F2937",
            borderColor: "#E5E7EB",
            errorColor: "#EF4444",
            successColor: "#10B981"
          }
        },

        modal: {
          closeOnOverlayClick: false,
          closeOnEsc: false
        },

        merchant: {
          name: "Transport 360",
          logo: logo,
        }
      };

      // Set a timeout for pending scenarios (30 seconds)
      timeoutId = setTimeout(() => {
        if (!isPaymentCompleted) {
          isPaymentCompleted = true;

          // Simulate pending status
          setVerifyLoading(true);
          setTimeout(() => {
            setVerifyLoading(false);
            setShowPending(true);
            toast.error("Payment is taking longer than expected. Please wait or contact support.");
            setTimeout(() => {
              setShowPending(false);
            }, 3000);
          }, 1000);
        }
      }, 30000); // 30 second timeout

      const result = await cashfree.checkout(checkoutOptions);

      // Clear timeout if payment completes normally
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      if (result && !isPaymentCompleted) {
        isPaymentCompleted = true;
        await paymentVerifyHandle(orderId);
      }

    } catch (error) {
      // Clear timeout on error
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      if (!isPaymentCompleted) {
        isPaymentCompleted = true;
        console.error("Cashfree SDK error:", error);
        toast.error("Payment system error. Please try again.");
        throw error;
      }
    }
  }, [paymentVerifyHandle]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      ...formData,
        company_id: StorageServices.getCompanyId(),
      selected_subscription_id: subscriptionDetails?._id,
      order_id: generateOrderId(),
      return_url:API_URL
    };

    try {
      const res = await createOrderPayment(payload);

      if (res?.responseCode === 200) {
        const { order_id, payment_session_id } = res.data;

        if (!payment_session_id) {
          toast.error("Failed to create payment session");
          return;
        }

        clearBillingAddress();
        setIsOpenBillingModal(false);

        await initializeCashfreePayment(payment_session_id, order_id);
      } else {
        toast.error(res?.message || "Failed to create order. Please try again.");
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [formData, subscriptionDetails?._id, generateOrderId, clearBillingAddress, initializeCashfreePayment]);

  const testSpecificPaymentState = useCallback((state) => {
    const mockOrderId = `test_order_${Date.now()}`;
    const statusMap = {
      'success': 1,
      'pending': 0,
      'failed': 2
    };

    paymentVerifyHandle(mockOrderId, true, statusMap[state]);
  }, [paymentVerifyHandle]);

  const testPendingPayment = useCallback(() => {
    testSpecificPaymentState('pending');
  }, [testSpecificPaymentState]);

  const testFailedPayment = useCallback(() => {
    testSpecificPaymentState('failed');
  }, [testSpecificPaymentState]);

  const testSuccessPayment = useCallback(() => {
    testSpecificPaymentState('success');
  }, [testSpecificPaymentState]);

  useEffect(() => {
    if (!subscriptionDetails?._id) {
      navigate("/subscription");
      return;
    }

    const gstRate = (subscriptionDetails?.gst_perc || 0) / GST_RATE_DIVISOR;
    const { plan_price, gst_price, total_amount } = calculateTotal(
      subscriptionDetails,
      branches,
      users,
      gstRate
    );

    setFormData((prev) => ({
      ...prev,
      plan_price,
      gst_price,
      total_amount,
    }));
  }, [subscriptionDetails, branches, users, calculateTotal, navigate]);

  const payBillModalProps = useMemo(() => ({
    isOpen: isOpenBillingModal,
    closeModal: () => {
      clearBillingAddress();
      setIsOpenBillingModal(false);
    },
    modalMainClass: "_dashCommon_Modal __payBil_modal",
    setFormData,
    formData,
    handleChange,
    handleSubmit,
    loading
  }), [isOpenBillingModal, clearBillingAddress, formData, handleChange, handleSubmit, loading]);

  if (!subscriptionDetails?._id) {
    return null;
  }

  const renderPaymentOverlays = () => (
    <>
      {/* Loading GIF - during verification */}
      {verifyLoading && (
        <div className="overlay">
          <div className="loader-container">
            <img
              className="loader"
              src={loadingGif}
              alt="Verifying Payment..."
            />
            <p className="overlay-text">Verifying your payment...</p>
          </div>
        </div>
      )}

      {/* Success GIF - payment successful */}
      {showSuccess && (
        <div className="overlay success-overlay">
          <div className="loader-container">
            <img
              src={successGif}
              alt="Payment Successful"
              className="loader"
              style={{ width: "200px", height: "200px" }}
            />
            <p className="overlay-text success-text">Payment Successful!</p>
          </div>
        </div>
      )}

      {/* Pending GIF - payment pending */}
      {/* {showPending && (
      <div className="overlay pending-overlay">
        <div className="loader-container">
          <img 
            src={pendingGif} 
            alt="Payment Pending" 
            className="loader"
            style={{ width: "200px", height: "200px" }}
          />
          <p className="overlay-text pending-text">Payment is pending...</p>
        </div>
      </div>
    )} */}

      {/* Failure GIF - payment failed */}
      {/* {showFailure && (
      <div className="overlay failure-overlay">
        <div className="loader-container">
          <img 
            src={failureGif} 
            alt="Payment Failed" 
            className="loader"
            style={{ width: "200px", height: "200px" }}
          />
          <p className="overlay-text failure-text">Payment Failed</p>
        </div>
      </div>
    )} */}
    </>
  );

  const overlayStyles = `
  .overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.4);
    backdrop-filter: blur(6px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    animation: fadeIn 0.3s ease-in;
  }

  .success-overlay {
    background: rgba(34, 197, 94, 0.1);
  }

  .pending-overlay {
    background: rgba(234, 179, 8, 0.1);
  }

  .failure-overlay {
    background: rgba(239, 68, 68, 0.1);
  }

  .loader-container {
    text-align: center;
    animation: slideUp 0.5s ease-out;
  }

  .loader {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 8px;
  }

  .overlay-text {
    margin-top: 16px;
    font-size: 18px;
    font-weight: 600;
    color: #374151;
  }

  .success-text {
    color: #059669;
  }

  .pending-text {
    color: #d97706;
  }

  .failure-text {
    color: #dc2626;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes slideUp {
    from { 
      transform: translateY(30px);
      opacity: 0;
    }
    to { 
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

  return (
    <>
      {renderPaymentOverlays()}
      <div className='__selPlan_mainBx'>
        <div className='__selPlan_container'>
          <div className='__selPlan_firstBx'>
            <div className="accordion" id="planAccordion">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <button
                    className="accordion-button d-flex align-items-center"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#selectedPlan"
                    aria-expanded="true"
                    aria-controls="selectedPlan"
                  >
                    <span className="__selPlan_checked"><FaCheck /></span>
                    <span className='__selPlan_acordHead'>Selected Plan</span>
                    <span className="__selPlan_badge">
                      <span><FaCrown /></span>
                      {subscriptionDetails.plan_name}
                    </span>
                  </button>
                </h2>
                <div id="selectedPlan" className="accordion-collapse collapse show">
                  <div className="accordion-body">
                    <div className="__selPlan__details">
                      <div>
                        <h5 className="__selPlan__deHead">{subscriptionDetails.plan_name}</h5>
                        <div className='__selPlBadge_bx'>
                          <span className="__selPlan__deBadge">1 year</span>
                          <span className="__selPlan__deBadge">
                            {`${subscriptionDetails.min_no_of_user} Users`}
                          </span>
                          <span className="__selPlan__deBadge">
                            {`${subscriptionDetails.no_of_branch} Branch`}
                          </span>
                        </div>
                      </div>
                      <div className="__selPlan__deAmt text-end">
                        <h5 className="__selPlan__dePrc">
                          {`₹ ${Number(subscriptionDetails.yearly_rate)}`}
                        </h5>
                        <small className="__selPlan__deAnnl">
                          {`@ ₹${subscriptionDetails.yearly_rate} / billed annually`}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="accordion-item">
                <h2 className="accordion-header">
                  <button
                    className="accordion-button"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#customisePlan"
                    aria-expanded="true"
                    aria-controls="customisePlan"
                  >
                    <span className='__selPlan_acordHead'>Customise Plan</span>
                  </button>
                </h2>
                <div id="customisePlan" className="accordion-collapse collapse show">
                  <div className="accordion-body">
                    <div className="__selPlan_customBx">
                      <div className='__delPlan_numTx'>
                        <p>Number of Branch</p>
                        <small>{`₹${subscriptionDetails.branch_rate.toFixed(2)} / Branch`}</small>
                      </div>
                      <div className="input-group">
                        <button
                          className="btn"
                          onClick={() => handleBranchChange(-1)}
                          disabled={branches === 0}
                        >
                          −
                        </button>
                        <input
                          type="text"
                          className="form-control text-center"
                          value={`${branches} Branch${branches !== 1 ? "es" : ""}`}
                          readOnly
                        />
                        <button
                          className="btn"
                          onClick={() => handleBranchChange(1)}
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <div className="__selPlan_customBx">
                      <div className='__delPlan_numTx'>
                        <p>Number of User</p>
                        <small>{`₹${subscriptionDetails.user_rate.toFixed(2)} / User`}</small>
                      </div>
                      <div className="input-group">
                        <button
                          className="btn"
                          onClick={() => handleUserChange(-1)}
                          disabled={users === 0}
                        >
                          −
                        </button>
                        <input
                          type="text"
                          className="form-control text-center"
                          value={`${users} User${users !== 1 ? "s" : ""}`}
                          readOnly
                        />
                        <button
                          className="btn"
                          onClick={() => handleUserChange(1)}
                          disabled={isUserLimitReached}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="accordion-item __notAccordion">
                <h2 className="accordion-header">
                  <button
                    className="accordion-button"
                    type="button"
                    data-bs-toggle="collapse"
                    aria-expanded="true"
                    aria-controls="billDetails"
                  >
                    <span className='__selPlan_acordHead'>Billing Details</span>
                  </button>
                </h2>
                <div className="accordion-collapse collapse show">
                  <div className="accordion-body">
                    <div className="__selPl_bilGst">
                      <span>I have GSTIN:</span>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={hasGST}
                          onChange={(e) => setHasGST(e.target.checked)}
                        />
                      </div>
                    </div>

                    <div className='__selInput_flx'>
                      {hasGST && (
                        <div className="__billInput_bx">
                          <CommonInput
                            placeholder="e.g., 27AABCCDDEEFFG"
                            label="Enter GST number"
                            value={formData.gst_details.gst_number}
                            onChange={(e) => handleChange('gst_details', 'gst_number', e.target.value)}
                          />
                        </div>
                      )}
                      <div className="__billInput_bx">
                        <CommonInput
                          placeholder="Enter company name"
                          label="Enter company name"
                          value={formData.gst_details.company_name}
                          onChange={(e) => handleChange('gst_details', 'company_name', e.target.value)}
                        />
                      </div>
                      <div className={`__billInput_bx ${hasGST ? "w-100" : ""}`}>
                        <CommonInput
                          placeholder="contact@company.com"
                          label="Enter email address"
                          type="email"
                          value={formData.gst_details.email_address}
                          onChange={(e) => handleChange('gst_details', 'email_address', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='__selPlan_secondBx'>
            <div className='__selPl_divider'></div>
          </div>

          <div className='__selPlan_thirdBx'>
            <div className="__selConfirm_planBx">
              <h5 className="__revHead mb-3">Review & Confirm Your Plan</h5>

              <div className='__flxr_seLplx'>
                <div className="__sled_pln_bxs">
                  <div className='__scr_prx'>
                    <div className="__sle_priceFlx_head">
                      <span>Plan Price (1 Year)</span>
                      <span>{`₹${formatIndianNumber(formData.plan_price)}`}</span>
                    </div>

                    {branches > 0 && (
                      <div className="__sle_priceFlx">
                        <span>{`${branches} Branch`}</span>
                        <span>{`₹${formatIndianNumber(subscriptionDetails.branch_rate * branches)}`}</span>
                      </div>
                    )}

                    {users > 0 && (
                      <div className="__sle_priceFlx">
                        <span>{`${users} User`}</span>
                        <span>{`₹${formatIndianNumber(subscriptionDetails.user_rate * users)}`}</span>
                      </div>
                    )}
                  </div>

                  <div className="__sle_gstFlx">
                    <span className='d-flex align-items-center gap-1.5'>
                      <span className='__sel_InfoBx'><RiInformationLine /></span>
                      {`GST ${subscriptionDetails.gst_perc}% of ₹${formatIndianNumber(formData.plan_price)}`}
                    </span>
                    <p>{`₹${formData.gst_price}`}</p>
                  </div>

                  <div className="__sel_totalFlx">
                    <h6 className="mb-0">Amount to Pay</h6>
                    <h5 className="text-primary mb-0">
                      {`₹${formatIndianNumber(formData.total_amount)}`}
                    </h5>
                  </div>
                </div>

                <div className="__refralBx">
                  <label className="form-label __revHead">
                    Apply Referral code or Discount code
                  </label>
                  <div className="input-group">
                    <div className='__inputRef'>
                      <img src={coupon} alt="coupon" />
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter code"
                        value={formData.coupon}
                        onChange={(e) => handleChange(null, 'coupon', e.target.value)}
                      />
                    </div>
                    <button className="__refApply_btn">Apply</button>
                  </div>
                </div>

                <div className='__selPay_btn'>
                  <button
                    className="anime-btn __payShield_btn"
                    onClick={() => setIsOpenBillingModal(true)}
                    disabled={loading}
                  >
                    <img src={shield} alt="shield" />
                    {`Pay Securely ₹${formatIndianNumber(formData.total_amount)}`}
                  </button>
                  <p>
                    {`Inclusive of all taxes (${subscriptionDetails.gst_perc}% GST)`}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      {isOpenBillingModal && (
        <PaymentBillingModal {...payBillModalProps} />
      )}

      {/* Inline styles inside the same file */}
      <style>{overlayStyles}</style>
    </>
  );
};

export default SubscriptionPlan;